import { useDialog } from '@globals/dialog/useDialogStore'
import { colors } from '@globals/themes/defaultTheme'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Slide from '@mui/material/Slide'
import Stack from '@mui/material/Stack'
import { SxProps } from '@mui/material/styles'
import { CustomDialog } from '@shared/dialog/CustomDialog'
import FormErrors from '@shared/forms/FormErrors'
import Submit from '@shared/forms/Submit'
import { RecognitionFormData } from '@tenant/recognition/RecognitionModal/sendRecognitionView/useSendRecognitionForm'
import { T } from '@transifex/react'
import { useFormContext, UseFormReturn } from 'react-hook-form'
import { DEFAULT_TITLE, mobileActionStyle } from '..'
import MobileRecognitionSendView from './MobileRecognitionSendView'

export const MOBILE_SEND_RECOGNITION_DRAWER_KEY = 'mobileSendRecognitionDrawerKey'

const sendDialogHeaderStyles: SxProps = {
  '& .MuiDialog-paper': {
    borderRadius: 0,
    border: 'none',
  },
  '& .CustomDialog-title': {
    background: `${colors.blueCardGradient} !important`,
    borderBottom: 'none !important',
    '& .MuiTypography-root': {
      color: `${colors.white} !important`,
    },
  },
  '& .CustomDialog-title .MuiButton-root': {
    color: `${colors.white} !important`,
  },
  '#mobileSendRecognitionDrawerKey-close': {
    '& svg': {
      color: `${colors.white} !important`,
    },
  },
}

type MobileRecognitionSendDialogProps = {
  formId: string
  form: UseFormReturn<RecognitionFormData>
  handleResetRecognitionForm: () => void
  handleScheduleRecognition: () => Promise<boolean>
  handleSaveAsDraft: () => void
  isPendingCreateOrSave: boolean
  isEditMode: boolean
  hasQualities: boolean
  onBeforeClose?: (doClose: () => void) => boolean
}

const MobileRecognitionSendDialog: React.FC<MobileRecognitionSendDialogProps> = ({
  formId,
  form,
  handleResetRecognitionForm,
  handleScheduleRecognition,
  handleSaveAsDraft,
  isPendingCreateOrSave,
  isEditMode,
  hasQualities,
  onBeforeClose,
}) => {
  const { closeDialog: closeSendRecognitionDialog } = useDialog(MOBILE_SEND_RECOGNITION_DRAWER_KEY)
  const { formState } = useFormContext<RecognitionFormData>()
  const scheduledAt = form.watch('scheduledAt')
  const isScheduledSend = !!scheduledAt

  const onScheduleRecognition = async () => {
    const isValid = await handleScheduleRecognition()
    if (isValid) handleDialogClose()
  }

  const onSaveAsDraft = () => {
    handleSaveAsDraft()
    handleDialogClose()
  }

  const handleDialogClose = () => {
    handleResetRecognitionForm()
    closeSendRecognitionDialog()
  }

  return (
    <CustomDialog
      dialogKey={MOBILE_SEND_RECOGNITION_DRAWER_KEY}
      title={DEFAULT_TITLE}
      backgroundColor={colors.grayUltraLight}
      fullScreen
      dialogStyles={sendDialogHeaderStyles}
      onClose={() => {
        if (onBeforeClose) {
          return onBeforeClose(handleDialogClose)
        }
        handleDialogClose()
      }}
      TransitionComponent={Slide}
      TransitionProps={{
        direction: 'up',
        timeout: { enter: 300, exit: 500 },
      }}>
      <Box
        sx={{
          py: 2,
          mb: isScheduledSend ? 7 : 14,
        }}>
        <MobileRecognitionSendView hasQualities={hasQualities} />
        <Box sx={{ ml: -2 }}>
          <FormErrors errors={formState.errors} />
        </Box>
      </Box>
      <Stack spacing={1.5} sx={mobileActionStyle}>
        {isScheduledSend ? (
          <Button
            id="btnSendScheduled"
            fullWidth
            variant="contained"
            disabled={isPendingCreateOrSave}
            onClick={onScheduleRecognition}>
            <T _str="Schedule" />
          </Button>
        ) : (
          <Submit<RecognitionFormData>
            elementId="btnSendRecognition"
            form={formId}
            formState={formState}
            fullWidth
            disabled={isPendingCreateOrSave}
            submitText={<T _str="Send" />}
            variant="contained"
          />
        )}
        {!isScheduledSend && (
          <Button
            fullWidth
            color="primary"
            id="btnSaveRecognitionAsDraft"
            variant="contained"
            disabled={isPendingCreateOrSave}
            sx={{ backgroundColor: colors.grayUltraLight, color: colors.bluePrimary }}
            onClick={onSaveAsDraft}>
            {isEditMode ? <T _str="Save" /> : <T _str="Save as draft" />}
          </Button>
        )}
      </Stack>
    </CustomDialog>
  )
}

export default MobileRecognitionSendDialog
